import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguagePrefix } from '@/hooks/useLanguagePrefix';
import ReactMarkdown from 'react-markdown';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [chatbotEnabled, setChatbotEnabled] = useState(false);
  const [showContactPrompt, setShowContactPrompt] = useState(false);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [disclaimerExpanded, setDisclaimerExpanded] = useState(false);
  const [disabledReason, setDisabledReason] = useState(null);
  const messagesEndRef = useRef(null);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const langPrefix = useLanguagePrefix();
  const location = useLocation();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Show on all pages EXCEPT Contact page (which has embedded chat)
  const isContactPage = location.pathname.includes('/contact');

  // Check if chatbot is enabled
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/chatbot/status`);
        const data = await response.json();
        setChatbotEnabled(data.enabled);
        setDisabledReason(data.disabled_reason);
      } catch (error) {
        console.error('Failed to check chatbot status:', error);
        setChatbotEnabled(false);
      }
    };
    checkStatus();
    
    // Check status every 30 seconds to detect auto-re-enable
    const interval = setInterval(checkStatus, 30000);
    return () => clearInterval(interval);
  }, [BACKEND_URL]);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Reset chat when language changes
  useEffect(() => {
    if (isOpen) {
      // Close chat and clear messages when language changes
      setIsOpen(false);
      setMessages([]);
      setShowContactPrompt(false);
      setDisclaimerExpanded(false);
    }
  }, [i18n.language]);

  // Send welcome message when chat opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessages = {
        en: `Welcome to Kitco for Institutions — the home of tokenized gold and silver, fully backed and blockchain-secured.
How can we help you today?`,
        fr: `Bienvenue chez Kitco pour les institutions — la référence pour l'or et l'argent tokenisés, entièrement garantis et sécurisés par blockchain.
Comment pouvons-nous vous aider aujourd'hui?`,
        es: `Bienvenido a Kitco para instituciones — el hogar del oro y plata tokenizados, completamente respaldados y asegurados por blockchain.
¿Cómo podemos ayudarle hoy?`
      };

      const disclaimers = {
        en: "This chatbot may occasionally provide inaccurate information. Please verify all details with our team.",
        fr: "Ce chatbot peut parfois fournir des informations inexactes. Veuillez vérifier auprès de notre équipe.",
        es: "Este chatbot puede proporcionar información inexacta. Verifique con nuestro equipo."
      };

      const quickActions = {
        en: [
          "Learn about DirectReserve™",
          "Learn about Precious Metals as a Service (PMaaS)",
          "Explore tokenized metals integration",
          "Speak with our institutional team"
        ],
        fr: [
          "En savoir plus sur DirectReserve™",
          "En savoir plus sur les métaux précieux en tant que service (PMaaS)",
          "Explorer l'intégration des métaux tokenisés",
          "Parler avec notre équipe institutionnelle"
        ],
        es: [
          "Conocer sobre DirectReserve™",
          "Conocer sobre Metales Preciosos como Servicio (PMaaS)",
          "Explorar la integración de metales tokenizados",
          "Hablar con nuestro equipo institucional"
        ]
      };
      
      setMessages([
        {
          role: 'assistant',
          content: welcomeMessages[i18n.language] || welcomeMessages.en,
          timestamp: new Date().toISOString(),
          quickActions: quickActions[i18n.language] || quickActions.en
        },
        {
          role: 'system',
          content: disclaimers[i18n.language] || disclaimers.en,
          timestamp: new Date().toISOString(),
          isCollapsible: true
        }
      ]);
    }
  }, [isOpen, i18n.language, messages.length]);

  const sendMessage = async (messageText = null) => {
    const textToSend = messageText || inputMessage;
    if (!textToSend || !String(textToSend).trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: String(textToSend).trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    if (!messageText) setInputMessage('');
    setIsLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/chatbot/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          message: String(textToSend).trim(),
          language: i18n.language
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const data = await response.json();

      const assistantMessage = {
        role: 'assistant',
        content: data.response,
        timestamp: new Date().toISOString(),
        redirect_to: data.redirect_to,
        lead_type: data.lead_type
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Show contact form prompt for qualified leads
      if (data.should_show_contact_form) {
        setShowContactPrompt(true);
      }

    } catch (error) {
      console.error('Chat error:', error);
      const errorMessages = {
        en: "I apologize, but I'm having trouble connecting. Please try again or contact us directly at info@kitcodigitalmetals.com",
        fr: "Je m'excuse, mais j'ai du mal à me connecter. Veuillez réessayer ou nous contacter directement à info@kitcodigitalmetals.com",
        es: "Lo siento, pero tengo problemas para conectarme. Por favor, inténtelo de nuevo o contáctenos directamente en info@kitcodigitalmetals.com"
      };

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: errorMessages[i18n.language] || errorMessages.en,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
    
    // Show "Start New Conversation" after 5+ messages
    if (messages.length >= 5) {
      setShowNewConversation(true);
    }
  };

  const startNewConversation = () => {
    // Generate new session ID
    setSessionId(`session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
    // Clear messages
    setMessages([]);
    // Reset states
    setShowContactPrompt(false);
    setShowNewConversation(false);
    setDisclaimerExpanded(false);
    setInputMessage('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const goToContact = () => {
    navigate(`${langPrefix}/contact`);
    setIsOpen(false);
  };

  const closeChat = async () => {
    // Send chat log if there were meaningful messages
    if (messages.length > 2) {
      try {
        await fetch(`${BACKEND_URL}/api/chatbot/end-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            session_id: sessionId,
            language: i18n.language
          }),
        });
      } catch (error) {
        console.error('Failed to send chat log:', error);
      }
    }
    setIsOpen(false);
  };

  // Don't render if chatbot is disabled OR if on contact page (has embedded chat)
  if (!chatbotEnabled || isContactPage) return null;

  return (
    <>
      {/* Chat Bubble Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-full p-4 shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 z-50 group"
          aria-label="Open chat"
        >
          <MessageCircle className="h-6 w-6" />
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
            !
          </span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-96 h-[600px] bg-white rounded-lg shadow-2xl flex flex-col z-50 border border-gray-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-black to-gray-900 text-white p-4 rounded-t-lg flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="bg-kitco-gold rounded-full p-2">
                <MessageCircle className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold">Kitco for Institutions</h3>
                <p className="text-xs text-grey-300">AI Assistant</p>
              </div>
            </div>
            <button
              onClick={closeChat}
              className="text-grey-300 hover:text-white transition-colors"
              aria-label="Close chat"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-grey-50">
            {messages.map((msg, index) => (
              <div key={index}>
                {msg.role === 'system' ? (
                  // System/Disclaimer message - Collapsible
                  <div className="bg-amber-50 border border-amber-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setDisclaimerExpanded(!disclaimerExpanded)}
                      className="w-full p-3 text-left flex items-center justify-between hover:bg-amber-100 transition-colors"
                    >
                      <span className="text-xs text-amber-900 font-semibold">
                        ⚠️ AI Disclaimer
                      </span>
                      <svg 
                        className={`h-4 w-4 text-amber-700 transition-transform ${disclaimerExpanded ? 'rotate-180' : ''}`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {disclaimerExpanded && (
                      <div className="px-3 pb-3">
                        <p className="text-xs text-amber-900 leading-relaxed">{msg.content}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  // User and Assistant messages
                  <>
                    <div
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg p-3 ${
                          msg.role === 'user'
                            ? 'bg-kitco-gold text-white'
                            : 'bg-white text-grey-600 border border-gray-200 shadow-sm'
                        }`}
                      >
                        {msg.role === 'user' ? (
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        ) : (
                          <div className="text-sm prose prose-sm max-w-none prose-a:text-kitco-gold prose-a:no-underline hover:prose-a:underline">
                            <ReactMarkdown>{msg.content}</ReactMarkdown>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Quick Action Buttons */}
                    {msg.quickActions && msg.role === 'assistant' && (
                      <div className="flex flex-col gap-2 mt-3">
                        {msg.quickActions.map((action, idx) => (
                          <button
                            key={idx}
                            onClick={() => sendMessage(action)}
                            disabled={isLoading}
                            className="text-left bg-white hover:bg-amber-50 border border-amber-200 hover:border-kitco-gold text-grey-600 text-sm px-4 py-2.5 rounded-lg transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          >
                            <span className="text-kitco-gold">▶</span>
                            <span>{action}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {/* External Link Button */}
                    {msg.redirect_to && msg.role === 'assistant' && (
                      <div className="flex justify-start mt-2">
                        <a
                          href={`https://${msg.redirect_to}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 bg-kitco-gold hover:bg-kitco-gold-hover text-white text-sm px-4 py-2 rounded-lg transition-colors"
                        >
                          {msg.lead_type === 'retail' 
                            ? (i18n.language === 'fr' ? 'Visiter la Boutique' : i18n.language === 'es' ? 'Visitar Tienda' : 'Visit Shop')
                            : (i18n.language === 'fr' ? 'Voir les Prix' : i18n.language === 'es' ? 'Ver Precios' : 'View Prices')}
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}


            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white text-grey-600 border border-gray-200 rounded-lg p-3 shadow-sm">
                  <Loader2 className="h-5 w-5 animate-spin text-kitco-gold" />
                </div>
              </div>
            )}

            {showContactPrompt && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-grey-600 mb-2">
                  {i18n.language === 'fr' 
                    ? "Souhaitez-vous nous contacter pour discuter de vos besoins institutionnels ?"
                    : i18n.language === 'es'
                    ? "¿Le gustaría contactarnos para discutir sus necesidades institucionales?"
                    : "Would you like to connect with our team to discuss your institutional needs?"}
                </p>
                <button
                  onClick={goToContact}
                  className="bg-kitco-gold text-white text-sm px-4 py-2 rounded hover:bg-kitco-gold-hover transition-colors w-full"
                >
                  {i18n.language === 'fr' 
                    ? "Nous Contacter"
                    : i18n.language === 'es'
                    ? "Contáctenos"
                    : "Contact Us"}
                </button>
              </div>
            )}

            {showNewConversation && (
              <div className="bg-grey-100 border border-gray-300 rounded-lg p-3 mt-2">
                <button
                  onClick={startNewConversation}
                  className="bg-black text-kitco-gold text-sm px-4 py-2 rounded hover:bg-grey-700 transition-colors w-full font-medium flex items-center justify-center gap-2"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {i18n.language === 'fr' 
                    ? "Nouvelle conversation"
                    : i18n.language === 'es'
                    ? "Nueva conversación"
                    : "Start New Conversation"}
                </button>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-200 bg-white rounded-b-lg">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={
                  i18n.language === 'fr'
                    ? "Tapez votre message..."
                    : i18n.language === 'es'
                    ? "Escribe tu mensaje..."
                    : "Type your message..."
                }
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                disabled={isLoading}
              />
              <button
                onClick={sendMessage}
                disabled={isLoading || !inputMessage.trim()}
                className="bg-kitco-gold text-white p-2 rounded-lg hover:bg-kitco-gold-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Send message"
              >
                <Send className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
